import { lazy } from "react";
import { Route, Routes } from "react-router-dom";
import AppLayout from "../appLayout/AppLayout";
import AdminLogin from "../../pages/adminLogin/AdminLogin";
import ProtectComponent from "../protectComponent/ProtectComponent";

// Every page is its own chunk, loaded when first opened: the whole admin app
// used to ship as one 1.1 MB file on every first visit. AppLayout shows a
// spinner inside the shell while a page's chunk arrives (its <Suspense>).
// The sign-in page, the layout and the guard stay in the entry bundle.
// These sit below every static import on purpose: CRA fails the build on
// eslint's import/first otherwise.
const WelcomeAdmin = lazy(() => import("../../pages/welcomeAdmin/WelcomeAdmin"));
const CustomerOrders = lazy(() => import("../../pages/customerOrders/CustomerOrders"));
const CustomerRequests = lazy(() => import("../../pages/customerRequests/CustomerRequests"));
const CreateNewCustomer = lazy(() => import("../../pages/customers/createNewCustomer/CreateNewCustomer"));
const CustomerList = lazy(() => import("../../pages/customers/customerList/CustomerList"));
const CustomerWalletTopUp = lazy(() => import("../../pages/additionalTasks/customerWalletTopUp/CustomerWalletTopUp"));
const CustomerProducts = lazy(() => import("../../pages/customerProducts/CustomerProducts"));
const HistoryPage = lazy(() => import("../../pages/history/historyPage"));
const GlobalLedger = lazy(() => import("../../pages/Transactions/transactions.js"));
const TopupRequests = lazy(() => import("../../pages/Transactions/topup wallet/TopupRequests"));
const SubscriptionRequests = lazy(() => import("../../pages/subscriptionRequests/SubscriptionRequests"));
const Sellers = lazy(() => import("../../pages/sellers/Sellers"));
const NotificationsPage = lazy(() => import("../../pages/notifications/NotificationsPage"));

// Order Group
const OrderCancellationRequests = lazy(() => import("../../pages/customerOrders/OrderCancellationRequests/OrderCancellationRequests"));
const CreateOrder = lazy(() => import("../../pages/customerOrders/CreateOrder/CreateOrder.js"));
const CancelledOrders = lazy(() => import("../../pages/customerOrders/CancelledOrders/CancelledOrders.js"));

// Sourcing Group
const CreateRequest = lazy(() => import("../../pages/customerRequests/CreateRequests/CreateRequest.js"));
const InventoryCancellationRequests = lazy(() => import("../../pages/customerRequests/cancellationRequests/InventoryCancellationRequests"));
const RestockRequests = lazy(() => import("../../pages/customerRequests/RestockRequests/restockrequest.js"));

// Inventory Group
const InventoryDeletionRequests = lazy(() => import("../../pages/customerProducts/InventoryDeletionRequests/InventoryDeletionRequests.js"));
const DeletedProducts = lazy(() => import("../../pages/customerProducts/DeletedProducts/DeletedProducts.js"));
const Integrations = lazy(() => import("../../pages/integrations/Integrations"));
const SyncJobs = lazy(() => import("../../pages/integrations/SyncJobs"));
const ZqSync = lazy(() => import("../../pages/zqSync/ZqSync"));
const SyncLog = lazy(() => import("../../pages/integrations/SyncLog"));
const HeldOrders = lazy(() => import("../../pages/integrations/HeldOrders"));

function Routing() {
  return (
    <Routes>
      <Route path="/" element={<AdminLogin />} />

      {/* Every route below shares one persistent sidebar/header — AppLayout
          is a layout route (renders <Outlet/>) instead of each page
          wrapping itself, so navigating between them no longer remounts the
          whole shell. ProtectComponent is no longer keyed by path either:
          it stays mounted and re-checks the session on each navigation,
          instead of remounting and flashing the loading screen. */}
      <Route element={<AppLayout />}>
        <Route path="/welcomeAdmin" element={ <ProtectComponent><WelcomeAdmin /></ProtectComponent> } />
        <Route path="/notifications" element={ <ProtectComponent><NotificationsPage /></ProtectComponent> } />

        {/* Orders Group */}
        <Route path="/customerOrders" element={ <ProtectComponent><CustomerOrders /></ProtectComponent> } />
        <Route path="/create-order" element={ <ProtectComponent><CreateOrder /></ProtectComponent> } />
        <Route path="/order-cancellation-requests" element={ <ProtectComponent><OrderCancellationRequests /></ProtectComponent> } />
        <Route path="/cancelled-orders" element={ <ProtectComponent><CancelledOrders /></ProtectComponent> } />

        {/* Sourcing Group */}
        <Route path="/customerRequests" element={ <ProtectComponent><CustomerRequests /></ProtectComponent> } />
        <Route path="/create-sourcing-request" element={ <ProtectComponent><CreateRequest /></ProtectComponent> } />
        <Route path="/restock-requests" element={ <ProtectComponent><RestockRequests /></ProtectComponent> } />
        <Route path="/inventory-cancellation-requests" element={ <ProtectComponent><InventoryCancellationRequests /></ProtectComponent> } />

        {/* Inventory Group */}
        <Route path="/customerProducts" element={ <ProtectComponent><CustomerProducts /></ProtectComponent> } />
        <Route path="/inventory-deletion-requests" element={ <ProtectComponent><InventoryDeletionRequests /></ProtectComponent> } />
        <Route path="/deleted-products" element={ <ProtectComponent><DeletedProducts /></ProtectComponent> } />

        {/* Customers Group */}
        <Route path="/customerList" element={ <ProtectComponent><CustomerList /></ProtectComponent> } />
        <Route path="/createNewCustomer" element={ <ProtectComponent><CreateNewCustomer /></ProtectComponent> } />

        {/* Finance Group */}
        <Route path="/customerWalletTopUp" element={ <ProtectComponent><CustomerWalletTopUp /></ProtectComponent> } />
        <Route path="/history" element={ <ProtectComponent><HistoryPage /></ProtectComponent> } />
        <Route path="/global-ledger" element={ <ProtectComponent><GlobalLedger /></ProtectComponent> } />
        <Route path="/topup-requests" element={ <ProtectComponent><TopupRequests /></ProtectComponent> } />

        {/* Every admin's own sellers */}
        {/* Store integrations (admin visibility). */}
        <Route path="/integrations" element={<ProtectComponent><Integrations /></ProtectComponent>} />
        <Route path="/integrationJobs" element={<ProtectComponent><SyncJobs /></ProtectComponent>} />
        <Route path="/send-to-zq" element={<ProtectComponent><ZqSync /></ProtectComponent>} />
        <Route path="/integrationSyncLog" element={<ProtectComponent><SyncLog /></ProtectComponent>} />
        <Route path="/integrationHeldOrders" element={<ProtectComponent><HeldOrders /></ProtectComponent>} />

        {/* Same page twice: the keys give each its own state, which the old
            per-path ProtectComponent key used to do. */}
        <Route path="/sellers" element={ <ProtectComponent><Sellers key="own" /></ProtectComponent> } />

        {/* Super-admin only (id 2 / Redoaune Khoulkhal) — server-side gated via require_super_admin.php */}
        <Route path="/subscriptionRequests" element={ <ProtectComponent><SubscriptionRequests /></ProtectComponent> } />
        <Route path="/allSellers" element={ <ProtectComponent><Sellers key="all" scopeAll={true} /></ProtectComponent> } />
      </Route>

      <Route path="*" element={<AdminLogin />} />
    </Routes>
  );
}

export default Routing;
